/** Decimal `batting_analysis` for one delivery (shot, where it went, how high). */
export type BattingAnalysis = {
  inns: number | null;
  /** Completed overs in the innings when the ball was bowled. */
  over: number | null;
  /** Legal balls in the current over, this one included. */
  balls: number | null;
  batter: string;
  shot: string;
  shotDir: string;
  aerial: string;
};

/** Latest ball results from the delta `score` list (`event.score` on our legacy event). */
export type BallResult = { ball: number; result: string };

const num = (v: unknown): number | null => {
  if (v == null || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const text = (v: unknown): string => {
  const s = v == null ? "" : String(v).trim();
  return s === "NA" || s === "0" ? "" : s;
};

/**
 * `batting_analysis` from a feed message: the wire message (`{ msg_type, batting_analysis }`),
 * the inner object, or a replay cache holding either. Null when there is no analysis.
 */
export function battingAnalysisFromMessage(msg: unknown): BattingAnalysis | null {
  let x = msg as Record<string, unknown> | null;
  for (let i = 0; i < 3 && x && typeof x === "object" && !x.analysis; i += 1) {
    x = (x.batting_analysis as Record<string, unknown> | undefined) ?? null;
  }
  if (!x || typeof x !== "object") return null;
  const a = x.analysis as Record<string, unknown> | undefined;
  if (!a || typeof a !== "object") return null;
  return {
    inns: num(x.inns),
    over: num(x.over),
    balls: num(x.balls),
    batter: text(x.batter),
    shot: text(a.shot),
    shotDir: text(a.shot_dir),
    aerial: text(a.aerial),
  };
}

/**
 * Ball results from the delta `score` list, newest first. `ball` is cricket notation plus a
 * delivery sequence (`42.40424` = ball 4 of over 43); `result` is Decimal's ball-result text
 * ("4 runs", "WICKET, Bowled", "Runs Likely").
 */
export function ballResultsFromEventScore(score: unknown): BallResult[] | null {
  if (!Array.isArray(score)) return null;
  const out: BallResult[] = [];
  for (const row of score) {
    const ball = num((row as { ball?: unknown })?.ball);
    const result = String((row as { result?: unknown })?.result ?? "").trim();
    if (ball != null && result) out.push({ ball, result });
  }
  return out.sort((a, b) => b.ball - a.ball);
}
